import React from 'react'
import {Link as RouteLink} from 'react-router-dom'
import PropTypes from 'prop-types'
import {Link, LinkOverlay, useStyleConfig} from '@chakra-ui/react'
import {useUrlFromLink} from '../../helpers/urlHelper'

/**
 * @typedef {Object} PDCLinkProps
 * @property {string} url - URL to link to
 * @property {string} label - Label to display
 * @property {boolean} isExternal - Whether the link is external
 * @property {string} fontColor - Font color
 * @property {string} fontSize - Font size
 * @property {string} displayConfig - Display configuration (style of the link)
 * @property {string} backgroundColor - Background color
 * @property {string} textColor - Text color
 * @property {string} txtHorizontalPos - Text horizontal position
 * @property {string} defaultColor - Default color
 * @returns {React.ReactElement} - PDCLink component.
 */
export const PDCLink = ({
    url,
    label,
    isExternal,
    fontColor,
    fontSize,
    displayConfig,
    backgroundColor,
    textColor,
    txtHorizontalPos,
    defaultColor,
    width,
    custom,
    children,
    ...props
}) => {
    const linkDisplayConfigVariant = {
        standard: '',
        'transparent button': 'secondary',
        'inverted button': 'primary'
    }
    const variant = displayConfig ? linkDisplayConfigVariant[displayConfig] : ''
    const isOverlay = custom?.isOverlay
    const styles = useStyleConfig('Link', {variant: variant, size: variant !== '' ? 'md' : 'auto'})

    /**
     * Determines the appropriate color based on the provided variant and color properties.
     *
     * This function evaluates the color to be used by checking the `variant` type and
     * the presence of color properties such as `fontColor`, `textColor`, `backgroundColor`,
     * and `defaultColor`. It returns a color string based on the following logic:
     *
     * - If the `variant` is 'primary':
     *   - Returns `backgroundColor` if either `fontColor` or `textColor` is truthy.
     *   - Otherwise, returns `defaultColor` or 'white' if `defaultColor` is falsy.
     * - For other variants:
     *   - Returns `fontColor`, `textColor`, or `defaultColor` if any are truthy.
     *   - Defaults to 'fullBlack' if none of the above are provided.
     *
     * @returns {string} The determined color string based on the input conditions.
     */
    const getColor = () => {
        if (variant === 'primary') {
            return fontColor || textColor ? backgroundColor : defaultColor || 'white'
        }
        return fontColor || textColor || defaultColor || 'fullBlack'
    }

    /**
     * Determines the border color based on the specified variant and available color options.
     *
     * @returns {string} - The resolved border color. If the variant is 'secondary', it returns
     *                     the first available color from `fontColor`, `textColor`, or `defaultColor`.
     *                     Otherwise, it returns `defaultColor` or defaults to 'fullBlack' if
     *                     `defaultColor` is not provided.
     *
     * @example
     * // Assuming variant is 'secondary' and fontColor is 'red'
     * getBorderColor(); // returns 'red'
     *
     * @example
     * // Assuming variant is not 'secondary' and defaultColor is 'blue'
     * getBorderColor(); // returns 'blue'
     *
     * @example
     * // Assuming variant is not 'secondary' and defaultColor is not provided
     * getBorderColor(); // returns 'fullBlack'
     */
    const getBorderColor = () => {
        if (variant === 'secondary') {
            return fontColor || textColor || defaultColor
        }
        return defaultColor || 'fullBlack'
    }

    /**
     * Determines the background color based on the specified variant and available color options.
     *
     * @returns {string} - Returns a color string representing the background color.
     *                     If the variant is 'primary', it returns the first available color from
     *                     the following options in order of priority: `fontColor`, `textColor`,
     *                     `defaultColor`, or defaults to 'fullBlack'. For any other variant,
     *                     it returns 'transparent'.
     */
    const getBackgroundColor = () => {
        if (variant === 'primary') {
            return fontColor || textColor || defaultColor || 'fullBlack'
        }
        return 'transparent'
    }

    // We remove 'typeId' prop coming from the component itself to avoid react warning
    const propsToMerge = Object.fromEntries(
        Object.entries(props).filter((prop) => prop[0] !== 'typeId')
    )

    const updatedUrl = useUrlFromLink(custom?.url) || url
    const _isExternal =
        (isExternal || custom?.url?.type === 'EXTERNAL') && !updatedUrl?.startsWith('/')
    const isAnchor = updatedUrl?.startsWith('#')

    // As this component is used in multiple contexts, we need to control the props from the parent
    // so we merge them with the default props and pass them to the link component
    const linkProps = {
        color: getColor(),
        borderColor: getBorderColor(),
        fontSize: fontSize || '12px',
        bg: getBackgroundColor(),
        variant: variant,
        textAlign: variant === '' ? txtHorizontalPos : '',
        ...propsToMerge
    }

    if (isAnchor) {
        Object.assign(linkProps, {
            href: updatedUrl,
            isExternal: false
        })
    } else if (_isExternal) {
        Object.assign(linkProps, {
            href: updatedUrl,
            isExternal: true
        })
    } else {
        Object.assign(linkProps, {
            to: updatedUrl,
            as: RouteLink,
            isExternal
        })
    }

    return (
        updatedUrl &&
        (isOverlay ? (
            <LinkOverlay
                width={width || 'auto'}
                display={variant === '' ? 'inline-block' : 'inline-flex'}
                {...styles}
                {...linkProps}
            >
                {label || children || updatedUrl}
            </LinkOverlay>
        ) : (
            <Link
                size={variant !== '' ? 'md' : 'auto'}
                width={width || 'auto'}
                display={variant === '' ? 'inline-block' : 'inline-flex'}
                {...linkProps}
            >
                {label || children || updatedUrl}
            </Link>
        ))
    )
}

PDCLink.propTypes = {
    url: PropTypes.string,
    label: PropTypes.string,
    isExternal: PropTypes.bool,
    fontColor: PropTypes.string,
    fontSize: PropTypes.string,
    displayConfig: PropTypes.string,
    backgroundColor: PropTypes.string,
    textColor: PropTypes.string,
    defaultColor: PropTypes.string,
    txtHorizontalPos: PropTypes.string,
    width: PropTypes.string,
    typeId: PropTypes.string,
    custom: PropTypes.shape({
        url: PropTypes.shape({
            target: PropTypes.string,
            type: PropTypes.string
        }),
        isOverlay: PropTypes.bool
    }),
    children: PropTypes.node
}
